import React, { useContext, useEffect, useRef, useState } from 'react'
import MonsterWindow from './subComponents/MonsterWindow/MonsterWindow'

import Cards from '../Cards/Cards.jsx'

import { CharacterContext } from '../../contexts/character'
import { SubStatsContext } from '../../contexts/subStats'
import { Player, Monster, defaultValue } from '../../constants/entity'
import { getBattleDamage } from '../../constants/battleFormulas'
import * as Calc from '../../constants/formulas'
// import skillInfoList from '../../data/skillInfo.json'

import { Item } from '../../contexts/databases'
import { getSkillRatio } from '../../constants/skillMod'
import { isSkillMagic } from '../../constants/battleOptions'
import SizePenalty from '../../constants/sizePenalty.json'
import SkillOption from '../../data/skill_db.json'
import SkillActivePassive from '../../data/skillActivePassive.json'
import * as jobID from '../../data/jobID.js'
import elementTable from '../../constants/elementTable.json'
import {
	BUFF_TREES,
	buffsIgnoreSizePenalty,
	selfBuffVisible,
	buffDisplayName,
} from '../../constants/buffs'
import { ComparisonContext } from '../../contexts/comparison'

import Select from 'react-select'

//css
import './battleTab.scss'

function BattleTab(props) {
	let {
		bonuses,
		inventory,
		skills,
		job,
		buffs,
		setBuffLevel,
		setBuffs,
		refreshPlayer,
	} = useContext(CharacterContext)

	const activeBuffCount = Object.values(buffs || {}).filter(
		(v) => Number(v) > 0,
	).length
	let { maxHP, maxSP, Def, softDef, Mdef, softMdef, hit, flee } =
		useContext(SubStatsContext)
	const { compared } = useContext(ComparisonContext)
	const [options, setOptions] = useState([])
	const [optionsSkillLv, setOptionsoptionsSkillLv] = useState([])
	const [currentSkill, setCurrentSkill] = useState('NV_BASIC')
	const [currentSkillLv, setCurrentSkillLv] = useState(Number(1))
	const [monster, setMonster] = useState(new Monster())
	const [currentPlayer, setCurrentPlayer] = useState()
	const [skillDamages, setSkillDamages] = useState([0, 0, 0])
	const [skillOption, setSkillOption] = useState(0)
	const skillRef = useRef()
	const skillLvRef = useRef()

	const tempLabel = ['Min', 'Ave', 'Max']

	const size = '25px'
	const customStyles = {
		control: (provided, state) => ({
			...provided,
			background: '#fff',
			borderColor: '#9e9e9e',
			minHeight: size,
			height: size,
			boxShadow: state.isFocused ? null : null,
		}),
		valueContainer: (provided, state) => ({
			...provided,
			height: size,
			padding: '0 6px',
		}),
		input: (provided, state) => ({
			...provided,
			margin: '0px',
		}),
		indicatorSeparator: (state) => ({
			display: 'none',
		}),
		indicatorsContainer: (provided, state) => ({
			...provided,
			height: size,
		}),
	}

	const getElementalMultiplier = (pBonuses = bonuses) => {
		let attackElement = 'Ele_Neutral'
		let searchedSkill = currentSkill
		let thisSkill = SkillOption.find(
			(skill) => skill.Name === searchedSkill,
		)
		let monsterElementLevel = monster?.ElementLevel
			? monster.ElementLevel
			: 1
		let monsterElement = monster?.Element
			? 'Ele_' + monster.Element
			: 'Ele_Neutral'
		//Because some monsters have Dark element and in element table it's named Shadow
		if (monsterElement === 'Ele_Dark') monsterElement = 'Ele_Shadow'
		if (thisSkill?.Element === 'Weapon' && pBonuses?.bAtkEle)
			attackElement = pBonuses.bAtkEle
		else if (thisSkill?.Element !== 'Weapon' && thisSkill?.Element)
			attackElement = 'Ele_' + thisSkill.Element
		return elementTable[monsterElementLevel][monsterElement][attackElement]
	}

	// Full skill-damage pipeline for a given player entity (current OR the mirror's
	// compared build) — ATK/MATK multipliers → base damage → per-hit battle damage.
	// Returns [min, ave, max]. Reused so current & compared are computed identically.
	const computeSkillDamages = (player) => {
		if (!player || !monster) return [0, 0, 0]
		const pB = player.Bonuses || {}
		const rightHand = defaultValue(
			player.Inventory?.['Right_Hand'],
			new Item('Right_Hand', true),
		)
		const raceKey =
			'RC_' + (monster?.Race === 'Demihuman' ? 'DemiHuman' : monster?.Race)
		const classKey = monster?.Class
			? 'Class_' + monster?.Class
			: 'Class_Normal'
		const eleKey = 'Ele_' + monster?.Element
		const sizeKey = 'Size_' + monster?.Size
		const elem = getElementalMultiplier(pB)

		const ATKMultipliers = {
			ATKMultiplier: defaultValue(pB.bAtkRate, 0),
			RaceMultiplier:
				defaultValue(pB.bAddRace?.RC_All, 0) +
				defaultValue(pB.bAddRace?.[raceKey], 0),
			SizeMultiplier:
				defaultValue(pB.bAddSize?.Size_All, 0) +
				defaultValue(pB.bAddSize?.[sizeKey], 0),
			TargetPropertyMultiplier:
				defaultValue(pB.bAddEle?.Ele_All, 0) +
				defaultValue(pB.bAddEle?.[eleKey], 0),
			MonsterMultiplier:
				defaultValue(pB.bAddRace2?.Class_All, 0) +
				defaultValue(pB.bAddRace2?.[eleKey], 0),
			ClassMultiplier:
				defaultValue(pB.bAddClass?.Class_All, 0) +
				defaultValue(pB.bAddClass?.[classKey], 0),
			PropertyMultiplier: 0,
			EDPMultiplier: 1,
		}
		const baseDamage = Calc.getBaseDamage(
			rightHand,
			defaultValue(player.baseLevel, 1),
			defaultValue(player.Str, 1),
			defaultValue(player.Dex, 1),
			defaultValue(player.Luk, 1),
			defaultValue(pB.bBaseAtk, 0) +
				defaultValue(pB.bAtk, 0) +
				defaultValue(player.Inventory?.['Ammo']?.Attack, 0),
			buffsIgnoreSizePenalty(buffs)
				? 100
				: defaultValue(
						SizePenalty.find(
							(w) => w.Weapon === rightHand.SubType,
						)?.[defaultValue(monster?.Size, 'Small')],
						100,
					),
			ATKMultipliers,
			elem,
		)

		const MATKMultipliers = {
			ATKMultiplier: defaultValue(pB.bMatkRate, 0),
			RaceMultiplier:
				defaultValue(pB.bMagicAddRace?.RC_All, 0) +
				defaultValue(pB.bMagicAddRace?.[raceKey], 0),
			SizeMultiplier:
				defaultValue(pB.bMagicAddSize?.Size_All, 0) +
				defaultValue(pB.bMagicAddSize?.[sizeKey], 0),
			TargetPropertyMultiplier:
				defaultValue(pB.bMagicAddEle?.Ele_All, 0) +
				defaultValue(pB.bMagicAddEle?.[eleKey], 0),
			MonsterMultiplier:
				defaultValue(pB.bMagicAddRace2?.Class_All, 0) +
				defaultValue(pB.bMagicAddRace2?.[eleKey], 0),
			ClassMultiplier:
				defaultValue(pB.bMagicAddClass?.Class_All, 0) +
				defaultValue(pB.bMagicAddClass?.[classKey], 0),
			PropertyMultiplier: defaultValue(
				pB.bMagicAtkEle?.[
					'Ele_' +
						SkillOption.find((s) => s.Name === currentSkill)?.Element
				],
				0,
			),
		}
		let magicDamage = Calc.getBaseMagicDamage(
			rightHand,
			defaultValue(player.baseLevel, 1),
			defaultValue(player.Int, 1),
			defaultValue(player.Dex, 1),
			defaultValue(player.Luk, 1),
			defaultValue(pB.bMatk, 0),
			elem,
			MATKMultipliers,
		)
		if (pB.bNoMatkVariance)
			magicDamage = [magicDamage[2], magicDamage[2], magicDamage[2]]

		const base = isSkillMagic(currentSkill) ? magicDamage : baseDamage
		return base.map((d) =>
			getBattleDamage(d, player, monster, currentSkill, currentSkillLv),
		)
	}

	useEffect(() => {
		setMonster(new Monster(0))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	useEffect(() => {
		// console.log('player', player)
		let newPlayer = convertPlayerToEntity(
			refreshPlayer(),
			{
				MaxHP: maxHP,
				MaxSP: maxSP,
				DEF: Def,
				SoftDEF: softDef,
				MDEF: Mdef,
				SoftMDEF: softMdef,
				Hit: hit,
				Flee: flee,
			},
			skills,
			bonuses,
		)
		setCurrentPlayer(newPlayer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		// player,
		skills,
		bonuses,
		flee,
		hit,
		inventory,
		maxHP,
		maxSP,
		softDef,
		softMdef,
	])

	//skill list option
	useEffect(() => {
		let newOptions = []
		//get Active skill list from skillActivePassive.json
		let skillList = getSkillList('Active', job)

		//add auto attack
		newOptions.push({
			value: '0',
			label: 'Auto Attack',
			key: 'skill_none',
		})
		skills
			.filter((skill) => skillList.includes(skill.AegisName))
			.forEach((skill) => {
				newOptions.push({
					value: skill.AegisName,
					label: skill.SkillName,
					key: 'skill_' + skill.AegisName,
				})
			})
		setOptions(newOptions)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [skills])
	//skill lv option
	useEffect(() => {
		let newOptions = []
		let maxSkillLv = skills.find(
			(skill) => skill.AegisName === currentSkill,
		)?.MaxLevel
		maxSkillLv = maxSkillLv ? maxSkillLv : 1
		for (let i = 1; i <= maxSkillLv; i++) {
			newOptions.push({
				value: i,
				label: i,
				key: 'skLv' + i,
			})
		}
		setSkillOption(SkillOption.find((skill) => skill.Name === currentSkill))

		setOptionsoptionsSkillLv(newOptions)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentSkill])
	useEffect(() => {
		let selectedSkill = SkillOption.find(
			(skill) => skill.Name === currentSkill,
		)
		if (typeof selectedSkill?.HitCount === 'number') {
			setSkillOption(selectedSkill.HitCount)
		} else if (typeof selectedSkill?.HitCount === 'object') {
			setSkillOption(
				selectedSkill.HitCount.find(
					(skill) => skill.Level === currentSkillLv,
				).Count,
			)
		} else setSkillOption(Number(1))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [skillOption, currentSkillLv])

	const createMonster = (mob) => {
		let newMonster = new Monster(mob)
		setMonster(newMonster)
	}

	useEffect(() => {
		setSkillDamages(computeSkillDamages(currentPlayer))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPlayer, monster, currentSkill, currentSkillLv, skillOption, buffs])
	//Reset Skill select on job change
	useEffect(() => {
		if (options.length > 0) skillRef.current?.selectOption(options[0])
	}, [options])
	//Reset Skill level select on Skill change
	useEffect(() => {
		if (optionsSkillLv.length > 0)
			skillLvRef.current?.selectOption(optionsSkillLv[0])
	}, [optionsSkillLv])

	// function handleBuffChange(event, currentbuff) {
	// 	let newbuffs = buffs
	// 	let buffIndex = buffs.findIndex((buff) => buff.name === currentbuff)
	// 	//security in case index is not found
	// 	if (~buffIndex) newbuffs[buffIndex].level = Number(event.target.value)
	// 	setBuffs(newbuffs)
	// }
	// Compared build's per-hit skill damage (mirror), same monster/skill/buffs.
	const comparedDamages = compared
		? computeSkillDamages(compared.player)
		: null
	return (
		// <div className={props.className} style={{ paddingTop: 20 + 'px' }}>
		// <div className={props.className} style={{ paddingRight: 25 + 'px' }}>
		<div className={props.className}>
			{/* // style={{ width: 350 + 20 + 2 + 'px', paddingTop: 20 + 'px' }} //350px wide + 2*10px padding + 2*1px border from MonsterWindow */}
			<div className='BattleWindow'>
				<Cards Name={'Battle tab'}>
					<div className='Results'>
						<div className='Monster'>
							<MonsterWindow onMonsterSelect={createMonster} />
						</div>
						<div>
							<div className='div'>
								<Select
									ref={skillRef}
									menuPosition='fixed'
									styles={customStyles}
									className='skill'
									onChange={(event) => {
										// console.log('event', event)
										setCurrentSkill(event.value)
									}}
									options={options}
								/>
								<Select
									ref={skillLvRef}
									menuPosition='fixed'
									defaultValue={{
										value: 1,
										label: 1,
										key: 'skLv' + 1,
									}}
									styles={customStyles}
									className='skillLV'
									onChange={(event) => {
										setCurrentSkillLv(Number(event.value))
									}}
									options={optionsSkillLv}
								/>
							</div>

							{/* <div> */}
							<div className='ActiveSkill'>
								<div className='SkillImgAndLevel'>
									<img
										src={
											'https://images.roprime-simulator.com/item/' +
											currentSkill.toLowerCase() +
											'.bmp'
										}
										alt={currentSkill}
									></img>
								</div>
								<div className='SkillRatio'>
									skill ratio : <br />
									{getSkillRatio(
										{ miscflag: 0 },
										currentSkill,
										currentSkillLv,
										currentPlayer,
										monster,
									)}
									%Atk
								</div>
							</div>
							{/* </div> */}
							<div className='DamageOutput'>
								{skillDamages.map((skillDamage, index) => {
									const hits = Math.abs(skillOption)
									const toTotal = (raw) =>
										(skillOption > 0
											? raw
											: Math.floor(raw / -skillOption)) *
										hits
									const perHit =
										skillOption > 0
											? skillDamage
											: Math.floor(
													skillDamage / -skillOption,
												)
									const total = perHit * hits
									const savedTotal = comparedDamages
										? toTotal(comparedDamages[index])
										: null
									const delta =
										savedTotal != null
											? total - savedTotal
											: 0
									return (
										<div
											className='DamageRow'
											key={'dmg' + index}
										>
											<span className='DamageLabel'>
												{tempLabel[index]}
											</span>
											<span className='DamageValue'>
												<span className='DamageTotal'>
													{total.toLocaleString(
														'fr-FR',
													)}
													{comparedDamages &&
														delta !== 0 && (
															<span
																className={
																	'DamageDelta ' +
																	(delta > 0
																		? 'up'
																		: 'down')
																}
															>
																{' ('}
																{delta > 0
																	? '+'
																	: '−'}
																{Math.abs(
																	delta,
																).toLocaleString(
																	'fr-FR',
																)}
																{')'}
															</span>
														)}
												</span>
												{skillOption !== 1 && (
													<span className='DamageBreakdown'>
														{hits} ×{' '}
														{perHit.toLocaleString(
															'fr-FR',
														)}
													</span>
												)}
											</span>
										</div>
									)
								})}
							</div>
						</div>
					</div>
				</Cards>
				<Cards Name={'Buffs'}>
					<div className='BuffToolbar'>
						<span className='buffCount'>
							{activeBuffCount > 0
								? activeBuffCount + ' active'
								: 'No buff active'}
						</span>
						{activeBuffCount > 0 && (
							<button
								className='clearBuffs'
								onClick={() => setBuffs({})}
							>
								Clear all
							</button>
						)}
					</div>
					<div className='BuffPanel'>
						{Object.entries(BUFF_TREES).map(
							([baseClass, groups]) => {
								// Self-only buffs are shown solely to the matching base
								// class / branch (independent of the skill database).
								const visibleGroups = Object.entries(groups)
									.map(([sub, list]) => [
										sub,
										list.filter(
											(buff) =>
												!buff.self ||
												selfBuffVisible(
													baseClass,
													sub,
													job,
												),
										),
									])
									.filter(([, list]) => list.length > 0)
								if (visibleGroups.length === 0) return null
								return (
									<div className='buffBase' key={baseClass}>
										<div className='buffBaseName'>
											{baseClass}
										</div>
										{visibleGroups.map(([sub, list]) => (
											<div className='buffTree' key={sub}>
												<div className='buffTreeName'>
													{sub}
												</div>
												{list.map((buff) => (
													<div
														className='buffRow'
														key={buff.skill}
													>
														<label
															title={
																buff.class +
																(buff.weapon
																	? ' — requires ' +
																		buff.weapon.join(
																			'/',
																		) +
																		' weapon'
																	: '') +
																(buff.approx
																	? ' — approximate scaling'
																	: '')
															}
														>
															{buffDisplayName(
																buff,
															)}
															{buff.approx
																? ' *'
																: ''}
														</label>
														<select
															value={
																buffs[
																	buff.skill
																] || 0
															}
															onChange={(event) =>
																setBuffLevel(
																	buff.skill,
																	event.target
																		.value,
																)
															}
														>
															{Array.from(
																{
																	length:
																		buff.maxLevel +
																		1,
																},
																(_, lv) => (
																	<option
																		key={lv}
																		value={
																			lv
																		}
																	>
																		{lv ===
																		0
																			? 'Off'
																			: 'Lv ' +
																				lv}
																	</option>
																),
															)}
														</select>
													</div>
												))}
											</div>
										))}
									</div>
								)
							},
						)}
					</div>
				</Cards>
			</div>
		</div>
	)
}

export default BattleTab
function getSkillList(SkillType, currentJob) {
	let skillList = []

	SkillActivePassive[jobID.getFirstClassName(currentJob)][SkillType].forEach(
		(sk) => skillList.push(sk),
	)

	if (jobID.isSecondJob(currentJob)) {
		SkillActivePassive[jobID.getSecondClassName(currentJob)][
			SkillType
		].forEach((sk) => skillList.push(sk))
	}

	if (jobID.isSecondJob(currentJob) && jobID.isRebirth(currentJob)) {
		SkillActivePassive[jobID.getSecondRebirthClassName(currentJob)][
			SkillType
		].forEach((sk) => skillList.push(sk))
	}

	if (jobID.isThirdJob(currentJob)) {
		SkillActivePassive[jobID.getThirdClassName(currentJob)][
			SkillType
		].forEach((sk) => skillList.push(sk))
	}
	return skillList
}
// function addNewBuff(currentbuff, keyname, handleBuffChange) {
// 	return (
// 		<div className='Skill' key={keyname + currentbuff}>
// 			<div className='SkillIconAndName'>
// 				<img
// 					src={
// 						'https://images.roprime-simulator.com/item/' +
// 						currentbuff.toLowerCase() +
// 						'.bmp'
// 					}
// 					alt=''
// 				/>
// 				<div>
// 					{
// 						SkillOption.find((skill) => skill.Name === currentbuff)
// 							.Description
// 					}
// 				</div>
// 			</div>
// 			<div className='select'>
// 				{createSelect(
// 					Object.entries(skillInfoList).find(
// 						(skill) => skill[1]['1'] === currentbuff,
// 					)[1],
// 					handleBuffChange,
// 				)}
// 			</div>
// 		</div>
// 	)
// }
// function addOptions(amount) {
// 	let options = []
// 	for (let i = 0; i <= amount; i++) options.push(i)

// 	return options
// }

// function createSelect(skill_, handleBuffChange) {
// 	return (
// 		<select onChange={(event) => handleBuffChange(event, skill_['1'])}>
// 			{addOptions(skill_?.MaxLv).map((buffLv) => (
// 				<option key={skill_['1'] + buffLv}>{buffLv}</option>
// 			))}
// 		</select>
// 	)
// }
function convertPlayerToEntity(player, substats, skills, bonuses) {
	let entityPlayer = new Player(
		player.BaseLevel,
		player.JobLevel,
		skills,
		substats,
		player.Stats,
		bonuses,
		player.Inventory,
	)
	// 	constructor(blv, jlv, skills, subStats, stats, bonuses, inventory) {
	// 	this.Stats = {
	// 	str: Stats.baseSTR ? Stats.baseSTR : 1,
	// 	agi: Stats.baseAGI ? Stats.baseAGI : 1,
	// 	vit: Stats.baseVIT ? Stats.baseVIT : 1,
	// 	int: Stats.baseINT ? Stats.baseINT : 1,
	// 	dex: Stats.baseDEX ? Stats.baseDEX : 1,
	// 	luk: Stats.baseLUK ? Stats.baseLUK : 1,
	// }
	// this.Job = job ? job : 0
	// this.BaseLevel = Blv ? Blv : 1
	// this.JobLevel = Jlv ? Jlv : 1
	// this.Inventory = Inventory
	return entityPlayer
}

// 		{
// 			MaxHP: maxHP,
// 			MaxSP: maxSP,
// 			DEF: Def,
// 			SoftDEF: softDef,
// 			MDEF: Mdef,
// 			SoftMDEF: softMdef,
// 			Hit: hit,
// 			Flee: flee,
// 		},
// 		{ Str: STR, Agi: AGI, Vit: VIT, Int: INT, Dex: DEX, Luk: LUK },
// 		bonuses,
